import { useState, useEffect, Fragment } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import { trpc } from 'root/src/client/v2/trpc/trpc';
import { useResetQueriesOnLogin } from 'root/src/client/v2/auth/hooks/useResetQueriesOnLogin';
import { useUserData } from 'root/src/client/v2/userData/hooks/useUserData';
import { useIsClipReviewer } from 'root/src/client/v2/auth/hooks/useIsClipReviewer';
import { useAuthentication } from 'root/src/client/v2/auth/hooks/useAuthentication';
import { useRedesignNavigation } from 'root/src/client/v2/layout/features/RedesignNavbar/useRedesignNavigation';
import { RedesignCommandSearchButton } from 'root/src/client/v2/layout/features/RedesignCommandPalette/RedesignCommandSearchButton';
import NextLink from 'next/link';
import { routeUrls } from 'root/src/shared/descriptions/routes/routeUrls';
import { Button } from 'root/src/client/v2/common/components/ui/button';
import { RedesignLink } from 'root/src/client/v2/common/components/ui/link';
import { Skeleton } from 'root/src/client/v2/common/components/ui/skeleton';
import { Popover, PopoverTrigger, PopoverContent } from 'root/src/client/v2/common/components/ui/popover';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from 'root/src/client/v2/common/components/ui/tooltip';
import { SignInDialog } from 'root/src/client/v2/auth/features/SignInDialog/SignInDialog';
import { buildAssetUrl } from 'root/src/client/v2/common/helpers/assetUrl';
import { cn } from 'root/src/client/v2/common/helpers/cn';
import { RedesignLogo } from 'root/src/client/v2/layout/components/RedesignLogo';
import { isMockReviewClipEnabled, MOCK_PENDING_REVIEWS } from 'root/src/client/v2/_pages/review-clips/redesign/mockClip';
import { NotificationBell } from 'root/src/client/v2/notifications/redesign/NotificationBell';
import { LanguageDropdown } from './LanguageDropdown';
const navLinkClass = 'shrink-0 whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-white/[0.06] hover:text-foreground';
// Shared by the desktop user popover and the mobile hamburger panel. On mobile it is a 44px tap
// target (Apple HIG / WCAG 2.5.5 minimum); from `md` up it collapses back to the compact desktop row.
const menuItemClass = 'flex min-h-[44px] w-full items-center gap-2.5 rounded-md px-2.5 py-2.5 text-left text-sm text-muted-foreground no-underline transition-colors hover:bg-white/[0.06] hover:text-foreground md:min-h-0 md:py-2';
// Mobile hamburger-panel nav link: matching 44px tap target.
const mobilePanelLinkClass = 'flex min-h-[44px] items-center rounded-md px-3 py-2.5 text-sm font-medium text-muted-foreground no-underline transition-colors hover:bg-white/[0.06] hover:text-foreground';
const reviewPerkChipClass = 'mt-2 inline-flex items-center gap-1.5 rounded bg-neon-green/10 px-1.5 py-0.5 text-[11px] font-semibold text-neon-green';
const Logo = () => (
// Plain <a> (full-page navigation), NOT RedesignLink/next-link: `/` server-side redirects to the
// Webflow marketing site via `res.writeHead`, which only fires on a real document request. A
// client-side next/link transition instead hits `/_next/data/...index.json`, where that same
// redirect breaks the data response (blank/broken page) instead of leaving to Webflow.
<a href="/" className="shrink-0 no-underline" aria-label="Dare Drop home">
		<RedesignLogo />
	</a>);
// Small glowing neon-pink count pill for the pending community reviews (mirrors the legacy PendingReviewsOverlay).
const PendingReviewsBadge = ({ count }) => (count > 0
    ? (<span className="inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-neon-pink px-1 text-[10px] font-bold leading-none text-white shadow-[0_0_10px_-1px_hsl(var(--neon-pink)/0.8)]">
				{count}
			</span>)
    : null);
// Top-level "Clips" nav entry shown to clip reviewers, with the live pending count and a tooltip
// describing the page - the redesign of the legacy additional nav link + PendingReviewsOverlay badge.
const ReviewClipsNavLink = ({ className, pendingReviews, onNavigate }) => (<TooltipProvider delayDuration={200}>
		<Tooltip>
			<NextLink href={routeUrls.reviewClips()} passHref>
				<TooltipTrigger asChild>
					<a onClick={onNavigate} className={cn(className, 'inline-flex items-center gap-1.5')}>
						<RateReviewOutlinedIcon sx={{ fontSize: 16, display: 'block' }}/>
						Clips
						<PendingReviewsBadge count={pendingReviews}/>
					</a>
				</TooltipTrigger>
			</NextLink>
			<TooltipContent className="max-w-[260px] p-3 text-left">
				<p className="font-display text-sm font-semibold text-foreground">Community Reviews</p>
				<p className="mt-1 text-xs leading-relaxed text-muted-foreground">
					Watch clips creators submitted for their dares and call each one: approve, feature, or reject.
				</p>
				<span className={reviewPerkChipClass}>
					<CardGiftcardIcon sx={{ fontSize: 12, display: 'block' }}/>
					Earn extra dares for reviewing
				</span>
			</TooltipContent>
		</Tooltip>
	</TooltipProvider>);
// Primary nav link with a hover/focus tooltip describing the destination page (same pattern as
// ReviewClipsNavLink). Used in both the desktop inline row and the mobile hamburger panel. Tooltips
// open on hover/focus only (Radix suppresses them on touch), so mobile taps just navigate.
const NavLinkWithTooltip = ({ href, label, description, className, onNavigate, newTab }) => (<TooltipProvider delayDuration={200}>
		<Tooltip>
			<NextLink href={href} passHref>
				<TooltipTrigger asChild>
					{/* target=_blank makes next/link fall back to a plain browser navigation, which the
        external /r/ (Webflow) links need. */}
					<a onClick={onNavigate} target={newTab ? '_blank' : undefined} rel={newTab ? 'noreferrer' : undefined} className={className}>{label}</a>
				</TooltipTrigger>
			</NextLink>
			<TooltipContent className="max-w-[260px] p-3 text-left">
				<p className="font-display text-sm font-semibold text-foreground">{label}</p>
				<p className="mt-1 text-xs leading-relaxed text-muted-foreground">{description}</p>
			</TooltipContent>
		</Tooltip>
	</TooltipProvider>);
// Mobile hamburger-panel nav link. Touch surfaces have no hover, so the page description is shown
// inline as a subtitle (a tooltip would never open on tap) rather than on hover like the desktop row.
const MobileNavLink = ({ href, label, description, onNavigate, newTab }) => (<RedesignLink href={href} onClick={onNavigate} target={newTab ? '_blank' : undefined} rel={newTab ? 'noreferrer' : undefined} className="group flex min-h-[44px] flex-col justify-center rounded-md px-3 py-2 no-underline transition-colors hover:bg-white/[0.06]">
		<span className="text-sm font-medium text-muted-foreground transition-colors group-hover:text-foreground">{label}</span>
		<span className="mt-0.5 text-[11px] leading-snug text-muted-foreground/60">{description}</span>
	</RedesignLink>);
// A navigable user-menu entry. On desktop (popover) the page description shows as a hover tooltip; in
// the mobile hamburger, which has no hover, it shows inline beneath the label (like the top nav links).
const UserMenuLink = ({ href, icon, label, description, variant, onNavigate, newTab }) => {
    // target=_blank makes next/link fall back to a plain browser navigation, which is exactly what
    // the external /r/ redirect links need (they are not Next routes).
    const newTabProps = newTab ? { target: '_blank', rel: 'noreferrer' } : {};
    if (variant === 'mobile') {
        return (<RedesignLink href={href} onClick={onNavigate} {...newTabProps} className="group flex min-h-[44px] items-start gap-2.5 rounded-md px-2.5 py-2 no-underline transition-colors hover:bg-white/[0.06]">
				<span className="mt-0.5 shrink-0 text-muted-foreground">{icon}</span>
				<span className="flex min-w-0 flex-col">
					<span className="text-sm text-muted-foreground transition-colors group-hover:text-foreground">{label}</span>
					{description ? <span className="mt-0.5 text-[11px] leading-snug text-muted-foreground/60">{description}</span> : null}
				</span>
			</RedesignLink>);
    }
    const link = (<RedesignLink href={href} onClick={onNavigate} {...newTabProps} className={menuItemClass}>
			{icon}
			{label}
		</RedesignLink>);
    if (!description) {
        return link;
    }
    return (<TooltipProvider delayDuration={200}>
			<Tooltip>
				<TooltipTrigger asChild>{link}</TooltipTrigger>
				<TooltipContent side="left" align="center" className="max-w-[240px] p-2.5 text-left">
					<p className="text-xs leading-relaxed text-muted-foreground">{description}</p>
				</TooltipContent>
			</Tooltip>
		</TooltipProvider>);
};
// Renders the shared account entries (from useRedesignNavigation): link entries as navigable menu
// links (hover tooltip on desktop, inline subtitle on mobile) and action entries as buttons. Sign-out
// gets the destructive styling plus a spinner while the async logout runs. onClose collapses the
// popover / mobile panel before the entry navigates or runs its handler.
const UserMenuItems = ({ items, loggingOut, onClose, variant = 'popover' }) => (<>
		{items.map((entry) => {
        const { Icon } = entry;
        const separator = entry.kind === 'action' && entry.separatorBefore
            ? <div className="my-1 h-px bg-white/[0.06]"/>
            : null;
        if (entry.kind === 'link') {
            return (<Fragment key={entry.id}>
						{separator}
						<UserMenuLink variant={variant} onNavigate={onClose} href={entry.href} icon={<Icon sx={{ fontSize: 16 }}/>} label={entry.label} description={entry.description} newTab={entry.newTab}/>
					</Fragment>);
        }
        const pending = entry.id === 'logout' && loggingOut;
        return (<Fragment key={entry.id}>
					{separator}
					<button type="button" onClick={() => { onClose(); entry.onSelect(); }} aria-busy={pending} className={cn(menuItemClass, entry.destructive && 'bg-destructive/10 text-destructive hover:bg-destructive/20 hover:text-destructive', pending && 'pointer-events-none opacity-70')}>
						{pending
                ? <span className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-solid border-current border-t-transparent"/>
                : <Icon sx={{ fontSize: 16 }}/>}
						{pending ? 'Signing out' : entry.label}
					</button>
				</Fragment>);
    })}
	</>);
export const RedesignNavbar = () => {
    var _a, _b, _c, _d, _e, _f;
    // Refetch all queries when the user logs in (redesign sign-in does not navigate), so the navbar and
    // the rest of the page stop showing logged-out / stale data without a manual reload.
    useResetQueriesOnLogin();
    const { userData, isLoading: userDataLoading } = useUserData();
    const { authentication } = useAuthentication();
    const { isClipReviewer } = useIsClipReviewer();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [mounted, setMounted] = useState(false);
    // TEMPORARY: `?mock=1` forces the Clips nav entry on (with a mock count) for previewing. Remove
    // this and the `isMock` usages below together with mockClip.ts.
    const isMock = mounted && isMockReviewClipEnabled();
    // Single source of truth for the top nav links + user menu, shared with the command palette so the
    // two can never drift. `forceClips` mirrors the `?mock=1` preview (show Clips without the role).
    const { primary, account, isAuthenticated, loggingOut } = useRedesignNavigation({ forceClips: isMock });
    // Poll the reviewer's pending-review count to badge the Clips nav entry (clip reviewers only).
    // Gated by the mounted flag so it does not run during SSR / for non-reviewers.
    const pendingReviewsQuery = trpc.communityReview.getPendingReviewsCount.useQuery(undefined, {
        enabled: mounted && isClipReviewer && !isMock,
        refetchInterval: 10 * 1000,
    });
    const pendingReviews = isMock ? MOCK_PENDING_REVIEWS : ((_a = pendingReviewsQuery.data) !== null && _a !== void 0 ? _a : 0);
    // Avoid the Sign In -> user flash: render a neutral placeholder until auth is resolved on the client.
    useEffect(() => {
        setMounted(true);
    }, []);
    const tokens = ((_b = userData === null || userData === void 0 ? void 0 : userData.tokens) !== null && _b !== void 0 ? _b : []);
    const token = (_c = tokens.find(item => item.isPreferred)) !== null && _c !== void 0 ? _c : tokens[0];
    const accountData = userData === null || userData === void 0 ? void 0 : userData.accountData;
    // Brands have no creator token, so fall back to their account (brand) name and logo.
    // When a publisher has not set up settings yet (empty brandName), show their email before the generic label.
    const brandLogoUrl = buildAssetUrl((_e = (_d = accountData === null || accountData === void 0 ? void 0 : accountData.brandLogo) === null || _d === void 0 ? void 0 : _d.small) !== null && _e !== void 0 ? _e : (_f = accountData === null || accountData === void 0 ? void 0 : accountData.brandLogo) === null || _f === void 0 ? void 0 : _f.big, { w: 56, q: 80, fmt: 'webp' });
    const displayName = (token === null || token === void 0 ? void 0 : token.displayName) || (token === null || token === void 0 ? void 0 : token.mnemonicId) || (accountData === null || accountData === void 0 ? void 0 : accountData.brandName) || (authentication === null || authentication === void 0 ? void 0 : authentication.email) || 'Account';
    const initial = displayName.charAt(0).toUpperCase();
    const avatarImage = (token === null || token === void 0 ? void 0 : token.image)
        ? <div className="h-7 w-7 rounded-full bg-cover bg-center" style={{ backgroundImage: `url(${token.image})` }} aria-hidden="true"/>
        : brandLogoUrl
            ? <div className="h-7 w-7 rounded-full bg-white/5 bg-contain bg-center bg-no-repeat" style={{ backgroundImage: `url(${brandLogoUrl})` }} aria-hidden="true"/>
            : <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-brand text-xs font-semibold text-white">{initial}</div>;
    const avatar = userDataLoading ? <Skeleton className="h-7 w-7 rounded-full"/> : avatarImage;
    return (<header className="fixed inset-x-0 top-0 z-[1090]">
			<div className="redesign mx-auto max-w-7xl px-4 pt-3 sm:px-6 lg:px-8">
				<nav className="flex items-center justify-between rounded-xl border border-solid border-white/[0.06] bg-card/90 px-4 py-2.5 backdrop-blur-xl sm:px-5">
					{/* Logo + primary nav links grouped on the left with an explicit gap, so the spacing between
            them is real (a margin here would be absorbed by the nav's justify-between distribution).
            The links can shrink + scroll on narrow widths; the logo never shrinks. */}
					<div className="flex min-w-0 items-center gap-10">
						<Logo />
						<div className="hidden min-w-0 items-center gap-0.5 overflow-x-auto scrollbar-hide mobile-l:flex">
							{primary.map((entry) => {
            // Clips is role/mock gated, so it resolves client-side; guard it behind `mounted` to
            // avoid a hydration mismatch and give it the special badge tooltip.
            if (entry.id === 'clips') {
                return mounted
                    ? <ReviewClipsNavLink key={entry.id} className={navLinkClass} pendingReviews={pendingReviews}/>
                    : null;
            }
            return (<NavLinkWithTooltip key={entry.id} href={entry.href} label={entry.label} description={entry.description} className={navLinkClass} newTab={entry.newTab}/>);
        })}
						</div>
					</div>

					<div className="hidden shrink-0 items-center gap-2 mobile-l:flex">
						<RedesignCommandSearchButton />
						<div className="hidden tablet:block">
							<LanguageDropdown />
						</div>
						{!mounted && (isAuthenticated ? (<div className="flex items-center gap-2 rounded-lg py-0.5 pl-1 pr-2">
								<Skeleton className="h-7 w-7 rounded-full"/>
								<Skeleton className="hidden h-3.5 w-24 tablet:block"/>
								<ExpandMoreIcon sx={{ fontSize: 16 }} className="text-muted-foreground/30"/>
							</div>) : (<Skeleton className="h-8 w-20 rounded-lg"/>))}
						{mounted && isAuthenticated && (<NotificationBell enabled={mounted && isAuthenticated}/>)}
						{mounted && isAuthenticated && (<Popover open={menuOpen} onOpenChange={setMenuOpen}>
								<PopoverTrigger asChild>
									<button type="button" className="flex items-center gap-2 rounded-lg py-0.5 pl-1 pr-2 text-sm transition-colors hover:bg-white/[0.06]">
										{avatar}
										{userDataLoading
                ? <Skeleton className="hidden h-3.5 w-24 tablet:block"/>
                : <span className="hidden max-w-[120px] truncate font-medium text-foreground tablet:inline">{displayName}</span>}
										<ExpandMoreIcon sx={{ fontSize: 16 }} className="text-muted-foreground"/>
									</button>
								</PopoverTrigger>
								{/* Radix reports the space left below the trigger in the available-height var; capping
            the content there + overflow-y keeps the grown role menus scrollable instead of
            running off the bottom edge on short screens. */}
								<PopoverContent align="end" collisionPadding={12} className="max-h-[var(--radix-popover-content-available-height)] w-56 space-y-1 overflow-y-auto p-1.5 dark-scrollbar">
									<UserMenuItems items={account} loggingOut={loggingOut} onClose={() => setMenuOpen(false)}/>
								</PopoverContent>
							</Popover>)}
						{mounted && !isAuthenticated && (<SignInDialog>
								<Button variant="brand" size="sm" className="h-8 rounded-lg px-4 text-sm font-medium">Sign In</Button>
							</SignInDialog>)}
					</div>

					<div className="flex items-center gap-1 mobile-l:hidden">
						<RedesignCommandSearchButton iconOnly/>
						{mounted && isAuthenticated && (<NotificationBell enabled={mounted && isAuthenticated}/>)}
					</div>

					<button type="button" className="flex h-8 w-8 items-center justify-center rounded-md text-foreground mobile-l:hidden" onClick={() => setMobileOpen(value => !value)} aria-label="Toggle menu">
						{mobileOpen ? <CloseIcon sx={{ fontSize: 20 }}/> : <MenuIcon sx={{ fontSize: 20 }}/>}
					</button>
				</nav>

				<AnimatePresence>
					{mobileOpen && (<motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.18, ease: 'easeOut' }} className="mt-1.5 max-h-[calc(100dvh-5.5rem)] overflow-y-auto rounded-xl border border-solid border-white/[0.06] bg-card/95 p-3 backdrop-blur-xl dark-scrollbar mobile-l:hidden">
							<div className="flex flex-col gap-1">
								{primary.map((entry) => {
                if (entry.id === 'clips') {
                    return mounted
                        ? (<ReviewClipsNavLink key={entry.id} className={mobilePanelLinkClass} pendingReviews={pendingReviews} onNavigate={() => setMobileOpen(false)}/>)
                        : null;
                }
                return (<MobileNavLink key={entry.id} href={entry.href} label={entry.label} description={entry.description} onNavigate={() => setMobileOpen(false)} newTab={entry.newTab}/>);
            })}
							</div>
							<div className="mt-2 space-y-1 border-t border-solid border-white/[0.06] pt-2">
								{!mounted && <Skeleton className="h-9 w-full rounded-lg"/>}
								{mounted && isAuthenticated && (<UserMenuItems variant="mobile" items={account} loggingOut={loggingOut} onClose={() => setMobileOpen(false)}/>)}
								{mounted && !isAuthenticated && (<SignInDialog>
										<Button variant="brand" size="sm" className="h-11 w-full rounded-lg px-4 text-sm font-medium">Sign In</Button>
									</SignInDialog>)}
							</div>
							<div className="mt-2 flex items-center justify-between border-t border-solid border-white/[0.06] pt-2">
								<span className="px-1 text-xs text-muted-foreground">Language</span>
								<LanguageDropdown />
							</div>
						</motion.div>)}
				</AnimatePresence>
			</div>

		</header>);
};
